import { useState } from 'react';
import { motion } from 'framer-motion';
import type { Easing } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import foto6 from '@/assets/brooklyn/foto6.webp';
import foto4 from '@/assets/brooklyn/foto4.webp';
import foto5 from '@/assets/brooklyn/foto5.webp';

const ease: Easing = 'easeOut';

const reveal = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.5, ease },
  style: { willChange: 'transform, opacity' as const },
};

interface CaseProps {
  index: string;
  name: string;
  role: string;
  descKey: string;
  image: string;
  reverse?: boolean;
}

const CaseItem = ({ index, name, role, descKey, image, reverse }: CaseProps) => {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  return (
    <div className="border-t border-border py-16 md:py-24">
      <div className={`flex flex-col ${reverse ? 'md:flex-row-reverse' : 'md:flex-row'} gap-10 md:gap-16 items-start`}>
        <div className="w-full md:w-1/2 flex flex-col justify-center order-1 md:order-none">
          <motion.span {...reveal} className="text-ui text-xs text-muted-foreground tracking-wider-ui mb-6">
            {index}
          </motion.span>
          <motion.h3 {...reveal} className="text-display text-4xl md:text-5xl text-foreground mb-3 relative inline-block">
            <span className="relative">
              {name}
              <span className="absolute bottom-0 left-0 w-0 h-px bg-foreground/40 transition-all duration-500 group-hover:w-full" />
            </span>
          </motion.h3>
          <motion.p {...reveal} className="text-ui text-xs text-muted-foreground tracking-wide-ui uppercase mb-4">
            {role}
          </motion.p>
          <motion.p {...reveal} className="text-sm text-muted-foreground leading-relaxed max-w-md">
            {t(descKey)}
          </motion.p>
        </div>
        <motion.div
          initial={{ opacity: 0, clipPath: 'inset(8% 8% 8% 8%)' }}
          whileInView={{ opacity: 1, clipPath: 'inset(0% 0% 0% 0%)' }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
          style={{ willChange: 'transform, opacity' }}
          className="w-full md:w-1/2 aspect-[3/4] bg-card overflow-hidden group order-2 md:order-none relative"
        >
          {!loaded && !errored && (
            <div className="absolute inset-0 bg-muted animate-pulse" aria-hidden="true" />
          )}
          <img
            src={errored ? '/placeholder.svg' : image}
            alt={`${name} — ${role}`}
            width={600}
            height={800}
            srcSet={!errored ? `${image} 600w, ${image} 400w` : undefined}
            sizes={!errored ? '(max-width: 768px) 100vw, 50vw' : undefined}
            className="w-full h-full object-cover transition-all duration-700 grayscale-[20%] md:group-hover:scale-[1.03] md:group-hover:brightness-75 md:group-hover:grayscale-0"
            loading="lazy"
            decoding="async"
            style={{ aspectRatio: '3/4' }}
            onLoad={() => setLoaded(true)}
            onError={() => { setErrored(true); setLoaded(true); }}
          />
        </motion.div>
      </div>
    </div>
  );
};

const ProjectsSection = () => {
  const { t } = useTranslation();

  const cases: CaseProps[] = [
    { index: '01', name: 'RAFAEL LIMA', role: 'Founder Presence', descKey: 'brooklyn.projects.desc1', image: foto6 },
    { index: '02', name: 'MOUSSE BAH', role: 'Editorial', descKey: 'brooklyn.projects.desc2', image: foto5, reverse: true },
    { index: '03', name: 'ALISSON', role: 'Runway', descKey: 'brooklyn.projects.desc3', image: foto4 },
  ];

  return (
    <section id="projetos" className="section-padding bg-background">
      <div className="content-width">
        <motion.h2 {...reveal} className="text-display text-section text-foreground mb-16 text-center md:text-left">
          {t('brooklyn.projects.title')}
        </motion.h2>
        {cases.map((c) => (
          <CaseItem key={c.index} {...c} />
        ))}
      </div>
    </section>
  );
};

export default ProjectsSection;
